import { BUTTON_SIZE, Button, Container, mediaQueries } from "@mozioinc/ui";
import { useSearch, useNavigate } from "@tanstack/react-router";
import Lottie from "lottie-react";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SearchForm, Layout, locationSchema } from "../containers";
import { getEnvs } from "../utils/env";
import { getWhiteLabel } from "../utils/white-label";
import affordableAnimation from "./assets/affordable.json";
import { ReactComponent as CancelationSvg } from "./assets/cancelation.svg";
import { ReactComponent as DiscountsSvg } from "./assets/discounts.svg";
import multipleServicesAnimation from "./assets/multiple-services.json";
import nearAirportAnimation from "./assets/near-airport.json";
import safetyAnimation from "./assets/safety.json";
import { ReactComponent as ServiceSvg } from "./assets/service.svg";
import type { TransNamespace } from "../types";
import type { AirportLocation } from "@mozioinc/query";
import type { LottieComponentProps, LottieRefCurrentProps } from "lottie-react";

const { ASSET_BASE_URL } = getEnvs();

const ISO_DATETIME_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2](?:\d:[0-5]){2}\d\.\d+([+-][0-2]\d:[0-5]\d|)/;

export const searchParamsSchema = z
  .object({
    location: locationSchema,
    departureDateTime: z.string().regex(ISO_DATETIME_REGEX),
    arrivalDateTime: z.string().regex(ISO_DATETIME_REGEX),
  })
  .partial();

const HomePage = () => {
  const { banners, heroImageUrl } = getWhiteLabel();
  const { t } = useTranslation<TransNamespace>("parking:home");
  // TODO: review these urls
  const esimUrl = "/esim";
  const transferUrl = location.origin.replace("parking.", "");
  const {
    location: defaultLocation,
    arrivalDateTime: defaultArrivalDateTime,
    departureDateTime: defaultDepartureDateTime,
  } = useSearch({ from: "/" });
  const navigate = useNavigate({ from: "/" });

  useEffect(() => {
    posthog.capture("Parking: Homepage Viewed");
  }, []);

  const bannersData = [
    {
      show: banners.showTransfers,
      picture: {
        srcSet: [
          `${ASSET_BASE_URL}/images/parking-transfers-mobile-200w.png 200w, ${ASSET_BASE_URL}/images/parking-transfers-mobile-777w.png 777w, ${ASSET_BASE_URL}/images/parking-transfers-mobile-1127w.png 1127w, ${ASSET_BASE_URL}/images/parking-transfers-mobile-1358w.png 1358w, ${ASSET_BASE_URL}/images/parking-transfers-mobile-1480w.png 1480w`,
          `${ASSET_BASE_URL}/images/parking-transfers-desktop-240w.png 240w, ${ASSET_BASE_URL}/images/parking-transfers-desktop-395w.png 395w, ${ASSET_BASE_URL}/images/parking-transfers-desktop-513w.png 513w, ${ASSET_BASE_URL}/images/parking-transfers-desktop-599w.png 599w, ${ASSET_BASE_URL}/images/parking-transfers-desktop-640w.png 640w`,
          `${ASSET_BASE_URL}/images/parking-transfers-desktop-1480w.png 1480w`,
        ],
        defaultSrc: `${ASSET_BASE_URL}/images/parking-transfers-mobile-777w.png`,
        alt: "",
      },
      title: t("Transfers"),
      description: t(
        "Travel with ease. Arrange for a driver to meet you at your destination.",
      ),
      url: transferUrl,
    },
    {
      show: banners.showESim,
      picture: {
        srcSet: [
          `${ASSET_BASE_URL}/images/parking-esim-mobile-200w.png 200w, ${ASSET_BASE_URL}/images/parking-esim-mobile-769w.png 769w, ${ASSET_BASE_URL}/images/parking-esim-mobile-1123w.png 1123w, ${ASSET_BASE_URL}/images/parking-esim-mobile-1358w.png 1358w, ${ASSET_BASE_URL}/images/parking-esim-mobile-1480w.png 1480w`,
          `${ASSET_BASE_URL}/images/parking-esim-desktop-240w.png 240w, ${ASSET_BASE_URL}/images/parking-esim-desktop-389w.png 389w, ${ASSET_BASE_URL}/images/parking-esim-desktop-505w.png 505w, ${ASSET_BASE_URL}/images/parking-esim-desktop-594w.png 594w, ${ASSET_BASE_URL}/images/parking-esim-desktop-640w.png 640w`,
        ],
        defaultSrc: `${ASSET_BASE_URL}/images/parking-esim-mobile-769w.png`,
        alt: "",
      },
      title: t("eSim"),
      description: t(
        "Stay connected without roaming fees. Our eSIM is available globally.",
      ),
      url: esimUrl,
    },
    {
      show: banners.showLounges,
      picture: {
        srcSet: [
          `${ASSET_BASE_URL}/images/parking-lounges-mobile-200w.png 200w, ${ASSET_BASE_URL}/images/parking-lounges-mobile-776w.png 776w, ${ASSET_BASE_URL}/images/parking-lounges-mobile-1080w.png 1080w, ${ASSET_BASE_URL}/images/parking-lounges-mobile-1325w.png 1325w, ${ASSET_BASE_URL}/images/parking-lounges-mobile-1480w.png 1480w`,
          `${ASSET_BASE_URL}/images/parking-lounges-desktop-240w.png 240w, ${ASSET_BASE_URL}/images/parking-lounges-desktop-378w.png 378w, ${ASSET_BASE_URL}/images/parking-lounges-desktop-501w.png 501w, ${ASSET_BASE_URL}/images/parking-lounges-desktop-588w.png 588w, ${ASSET_BASE_URL}/images/parking-lounges-desktop-640w.png 640w`,
        ],
        defaultSrc: `${ASSET_BASE_URL}/images/parking-lounges-mobile-769w.png`,
        alt: "",
      },
      title: t("Airport Lounges"),
      description: t("Relax with a hot drink in a peaceful lounge."),
      url: "https://pages.mozio.com/lounges",
    },
  ] as const;
  const bannersToShow = bannersData.filter((b) => b.show);

  const onLocationChange = (value: AirportLocation | null) => {
    if (value) {
      navigate({
        search: (prev) => ({
          ...prev,
          location: value,
        }),
        replace: true,
      });
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          location: undefined,
        }),
        replace: true,
      });
    }
  };

  const onDateTimeChange = (name: string, value: string) => {
    navigate({
      search: (search) => ({
        ...search,
        [name]: value,
      }),
    });
  };

  return (
    <Layout className="pt-0">
      <section className="relative">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <picture>
            <source
              srcSet={
                heroImageUrl ||
                `${ASSET_BASE_URL}/images/parking-hero-desktop-768w.png 768w, ${ASSET_BASE_URL}/images/parking-hero-desktop-1542w.png 1542w, ${ASSET_BASE_URL}/images/parking-hero-desktop-2058w.png 2058w, ${ASSET_BASE_URL}/images/parking-hero-desktop-2509w.png 2509w, ${ASSET_BASE_URL}/images/parking-hero-desktop-2880w.png 2880w`
              }
              media={mediaQueries.md}
            />
            <source
              srcSet={
                heroImageUrl ||
                `${ASSET_BASE_URL}/images/parking-hero-mobile-200w.png 200w, ${ASSET_BASE_URL}/images/parking-hero-mobile-717w.png 717w, ${ASSET_BASE_URL}/images/parking-hero-mobile-1035w.png 1035w, ${ASSET_BASE_URL}/images/parking-hero-mobile-1308w.png 1308w, ${ASSET_BASE_URL}/images/parking-hero-mobile-1536w.png 1536w`
              }
            />
            <img
              className="absolute inset-0 h-full w-full object-cover object-top md:object-center"
              src={
                heroImageUrl ||
                `${ASSET_BASE_URL}/images/parking-hero-mobile-717w.png`
              }
              alt="Parking lot with cars."
            />
          </picture>
          <div className="bg-primary-400/40 absolute inset-0" />
        </div>

        <Container className="pb-36 pt-24 md:pt-32">
          <h1 className="px-3 text-2xl !leading-tight text-white md:text-5xl">
            {t("Driving to the airport?")}
            <br />
            <span className="font-bold">
              {t("Prebook your parking and guarantee your space")}
            </span>
          </h1>

          <section className="mt-11 rounded-lg bg-neutral-100 p-5">
            <SearchForm
              defaultLocation={defaultLocation}
              defaultArrivalDateTime={defaultArrivalDateTime}
              defaultDepartureDateTime={defaultDepartureDateTime}
              onLocationChange={onLocationChange}
              onDateTimeChange={onDateTimeChange}
            />
          </section>
        </Container>
      </section>
      <section className="bg-primary text-primary-contrast">
        <Container className="flex flex-wrap gap-x-9 gap-y-2 p-5 text-lg sm:justify-center md:py-10 [&_svg]:text-6xl">
          <div className="flex items-center gap-4">
            <ServiceSvg />
            {t("24/7 Customer service")}
          </div>
          <div className="flex items-center gap-4">
            <CancelationSvg />
            {t("Flexible cancellation policy")}
          </div>
          <div className="flex items-center gap-4">
            <DiscountsSvg />
            {t("Special discounts")}
          </div>
        </Container>
      </section>
      <section className="bg-neutral-100">
        <Container className="py-10 md:py-20">
          <h2 className="px-3 text-2xl font-bold md:text-4xl lg:text-5xl">
            {t("Using our airport parking network")}
          </h2>

          <div className="mt-5 grid grid-flow-col grid-rows-4 gap-2 md:grid-rows-2 lg:mt-12 lg:grid-flow-row lg:grid-cols-4 lg:grid-rows-1 lg:gap-5">
            {(
              [
                [
                  nearAirportAnimation,
                  t("Near the airport"),
                  t(
                    "Park near the airport and save money. Choose from spots within walking distance or take a free shuttle bus.",
                  ),
                ],
                [
                  affordableAnimation,
                  t("Affordable rates"),
                  t(
                    "Prebook your parking spot and pay less. You will have your guaranteed parking spot waiting for you at the airport.",
                  ),
                ],
                [
                  safetyAnimation,
                  t("Safety first"),
                  t(
                    "We secure all our parking areas, control access, and provide 24/7 onsite security.",
                  ),
                ],
                [
                  multipleServicesAnimation,
                  t("Extra Services"),
                  t(
                    "Find the right parking for you. Options include meet and greet, luggage help, and constant security.",
                  ),
                ],
              ] as const
            ).map(([animation, title, description]) => (
              <ParkingFeatureCard
                key={title}
                animationData={animation}
                title={title}
                description={description}
              />
            ))}
          </div>
        </Container>
      </section>
      <section>
        <Container className="py-10 md:py-20">
          <div className="px-3">
            <div className="bg-secondary text-secondary-contrast w-max rounded-lg px-2 py-1 text-lg md:px-4 md:text-2xl">
              {t("More travel services")}
            </div>
            <h2 className="mt-6 text-2xl font-bold md:text-4xl lg:text-5xl">
              {t("Effortless Travel")}
            </h2>
            <p className="mt-1 text-xl text-neutral-800 md:text-2xl">
              {t("Enjoy a hassle-free holiday with top-quality services.")}
            </p>
          </div>

          <div
            className={`mt-3 grid gap-2 md:mt-5 grid-cols-${bannersToShow.length} md:gap-5`}
          >
            {bannersToShow.map(({ picture, title, description, url }) => (
              <div
                key={title}
                className="relative grid h-60 grid-flow-row grid-rows-[auto_1fr_auto] justify-stretch overflow-hidden rounded-2xl px-3 py-5 text-white md:h-[30rem] md:p-5"
              >
                <div className="absolute inset-0 -z-10">
                  <picture>
                    <source
                      srcSet={
                        bannersToShow.length === 3
                          ? picture.srcSet[1]
                          : picture.srcSet[2]
                      }
                      media={mediaQueries.md}
                    />
                    <source srcSet={picture.srcSet[0]} />
                    <img
                      className="absolute inset-0 h-full w-full object-cover object-top"
                      src={picture.defaultSrc}
                      alt={picture.alt}
                    />
                  </picture>
                  <div className="bg-primary-400/50 absolute inset-0" />
                </div>

                <h3 className="text-2xl font-bold md:text-3xl">{title}</h3>
                <p className="text-lg md:mt-3 md:text-2xl">{description}</p>
                <Button
                  size={BUTTON_SIZE.LG}
                  className="justify-self-start"
                  asChild
                >
                  <a href={url}>{t("Learn more")}</a>
                </Button>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </Layout>
  );
};

type ParkingFeatureCardProps = {
  animationData: LottieComponentProps["animationData"];
  title: string;
  description: string;
  className?: string;
};
const ParkingFeatureCard = ({
  animationData,
  title,
  description,
}: ParkingFeatureCardProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  return (
    <div
      key={title}
      className="grid auto-rows-min grid-cols-[auto_1fr] gap-x-3 rounded-2xl bg-white p-4 lg:grid-cols-1 lg:gap-4 lg:py-12"
      onPointerEnter={() => lottieRef.current?.play()}
      onPointerLeave={() => lottieRef.current?.stop()}
    >
      <div className="[&_path]:stroke-primary row-span-2 h-24 w-24 stroke-black lg:h-36 lg:w-36 lg:justify-self-center">
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={false}
          autoplay={false}
        />
      </div>
      <h3 className="text-lg font-bold lg:mt-6 lg:text-2xl">{title}</h3>
      <p className="text-sm">{description}</p>
    </div>
  );
};

export default HomePage;
